import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { getBaseApiService, IBaseApiService } from './baseApiService';

export type TLastActivitiesEndpoint = 'training_progress' | 'users_stats';

interface ILastActivity {
  uid: number;
  endpoint: TLastActivitiesEndpoint;
  updated_at: number;
}

type TLastActivitiesResponse = {
  activities: Array<ILastActivity>;
};

export interface IApiService {
  action: IBaseApiService['action'];
  setPrivateConfig: (config: Map<string, string>) => void;
  init: (ApiUrl: string) => void;
  setUserData: (uId: number, token: string, tokenCallback: () => void) => void;
  getUserId: () => number;
  getLastActivity: (endpoint: TLastActivitiesEndpoint) => Promise<number | undefined>;
  addAuthInterceptor: () => void;
  addRetry: () => void;
}

function getAxiosInstance() {
  return axios.create();
}

const axiosInstance = getAxiosInstance();

function findLastActivityInResponse(
  endpoint: TLastActivitiesEndpoint,
  userId?: number,
  response?: TLastActivitiesResponse
) {
  if (!response || !userId) {
    return;
  }
  return response.activities.find((act) => act.endpoint === endpoint && act.uid === userId);
}

export function createApiService(override?: AxiosInstance): IApiService {
  let api: IBaseApiService;
  let userId: number | undefined;
  let refreshToken: () => void;
  let tokenExpired = false;
  const instance = override ? override : axiosInstance;

  const init = (ApiUrl: string) => {
    const apiConfigUrl = `${ApiUrl}/config?appVersion=3.0.0`;
    api = getBaseApiService(apiConfigUrl, instance);
  };

  const setUserData = (uId: number, token: string, tokenCallback: () => void) => {
    userId = uId;
    // Save the callbak for token refresh
    refreshToken = tokenCallback;
    if (!api) {
      throw new Error('API setUserData call before init');
    }
    api.setToken(token);
    tokenExpired = false;
  };

  const getUserId = () => {
    if (typeof userId === 'undefined') {
      throw new Error('UserId is undefined');
    }
    return userId;
  };

  function handleAuthError(error: AxiosError) {
    if (error.response && error.response.status === 401) {
      // set expired token flag
      tokenExpired = true;
      // redirect to sso
      return refreshToken();
    }
    return Promise.reject(error);
  }

  function checkForExpiredToken(config: InternalAxiosRequestConfig) {
    // if token expired and authorization header is set - deny the request
    if (tokenExpired && config.headers?.Authorization) {
      throw new AxiosError('Authorization expired!', 'AUTHORIZATION_EXPIRED', config);
    }
    return config;
  }

  function addAuthInterceptor() {
    instance.interceptors.response.use(undefined, handleAuthError);
    instance.interceptors.request.use(checkForExpiredToken);
  }

  function addRetry() {
    axiosRetry(instance, { retries: 5, retryDelay: axiosRetry.exponentialDelay });
  }

  let cachedActivities: TLastActivitiesResponse;
  const getLastActivity = async (endpoint: TLastActivitiesEndpoint) => {
    const cachedValue = findLastActivityInResponse(endpoint, userId, cachedActivities);
    if (cachedValue) {
      return cachedValue.updated_at;
    }
    const apiResponse = await api.action<TLastActivitiesResponse>('user_last_activities', {
      method: 'get',
      urlParams: {
        userId: getUserId(),
      },
    });
    cachedActivities = apiResponse.data;
    const lastActivity = findLastActivityInResponse(endpoint, userId, cachedActivities);
    return lastActivity?.updated_at;
  };

  const service = {
    action: <T>(action: string, options: any) => {
      if (!api) {
        throw new Error('API action call before init');
      }
      return api.action<T>(action, options);
    },
    setPrivateConfig: (config: Map<string, string>) => {
      if (!api) {
        throw new Error('API must be initialized first');
      }
      return api.setPrivateConfig(config);
    },
    init,
    setUserData,
    getUserId,
    getLastActivity,
    addAuthInterceptor,
    addRetry,
  };
  return service;
}

const ApiService = createApiService();

// reexport interceptors as standalone funciton for backward compatibility
export const addAuthInterceptor = ApiService.addAuthInterceptor;
export const addRetry = ApiService.addRetry;

export default ApiService;
